import React, {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {EyeSlash} from '../icons';
import {useUncontrolledSpendAccess} from '../../context/UserModulesContext';
import {formatCompactMoney} from '../client/panel_v11/panelFormatters';
import {hasConvertedAmount, levelModifier, withResolutions} from './uncoveredSpend';
import {useUncoveredResolutions} from './uncoveredResolutions';
import {useUncoveredActions} from './useUncoveredActions';
import {UncoveredSpendPopover} from './UncoveredSpendPopover';

const RAW = {interpolation: {escapeValue: false}};

/**
 * «No cuenta 2 campañas · CLP 1,2M» — la inversión que la regla de campañas de la línea deja afuera y que
 * no cuenta ninguna otra línea (caso Kendal: la regla «no contar las que contienen “cyber”» dejó afuera el
 * Cyber de octubre y nadie lo veía). Un solo componente en dos lugares: la fila del Tablero (línea y cuenta)
 * y la fila de la línea en el panel del cliente.
 *
 * - Quien lo monta le pasa la FUGA DE LA REGLA (`ruleLeakOf`, tanda «Inversión sin línea»): lo que no cuenta
 *   ninguna línea en una cuenta vive en «Inversión sin control» (UD-5), adonde lleva el link del popover.
 * - Sólo existe si `uncoveredSpend` viene y su monto es mayor que 0: con el backend viejo (sin el campo)
 *   o una cuenta en «todas las campañas» (`null`), la fila es la de siempre.
 * - El color es el semáforo del Tablero según `level` (crítico / medio / normal), y nunca va solo: el
 *   texto dice el monto y cuántas campañas.
 * - El clic abre el popover con la regla, las campañas y las salidas. Lo que se resuelve ahí se descuenta
 *   en el acto en TODA la pestaña (`uncoveredResolutions.js`); el dato del backend cambia recién en el
 *   próximo sync, y el toast lo dice. Si no queda nada, el chip desaparece.
 *
 * `accounts`: las cuentas que ya tiene la pantalla (`{id, channel, account_id, name}`), para el nombre de
 * cada cuenta cuando la regla no lo trae. `rowClassName`: el renglón propio donde lo monta cada pantalla; va
 * acá adentro para que, si el usuario resuelve todo, no quede vacío.
 */
export function UncoveredSpendChip({uncoveredSpend, accounts, businessUnitId, rowClassName = null}) {
    const {t} = useTranslation();
    const {enabled: uncontrolledSpendEnabled} = useUncontrolledSpendAccess();
    const chipRef = useRef(null);
    const id = useId();
    const [open, setOpen] = useState(false);
    const resolved = useUncoveredResolutions();
    const {busyKey, dismiss} = useUncoveredActions();

    const current = useMemo(() => withResolutions(uncoveredSpend, resolved, accounts),
        [uncoveredSpend, resolved, accounts]);

    const close = useCallback(({returnFocus} = {}) => {
        setOpen(false);
        if (returnFocus && chipRef.current) chipRef.current.focus();
    }, []);

    // Si lo resuelto vació el chip, el popover se cierra: si no, «Deshacer» lo hacía volver abierto, robando el
    // foco (review C-cs-fe-12).
    useEffect(() => {
        if (!current && open) setOpen(false);
    }, [current, open]);

    if (!current) return null;

    const toggle = (event) => {
        // En el panel del cliente tocar la fila la expande: abrir el chip no la tiene que plegar.
        event.stopPropagation();
        setOpen((isOpen) => !isOpen);
    };

    const tipId = `${id}-tip`;
    const popoverId = `${id}-popover`;
    // Todo en otra moneda que no se pudo convertir (`amount` en 0, C-10): el chip cuenta campañas sin monto.
    const label = hasConvertedAmount(current)
        ? t('line_uncovered.chip', {
            count: current.campaign_count,
            amount: formatCompactMoney(current.currency, current.amount),
            ...RAW,
        })
        : t('line_uncovered.chip_no_amount', {count: current.campaign_count});

    const chip = (
        <span className={`usp-chip-wrap${open ? ' -open' : ''}`}>
            {/* Con la alerta silenciada (`is_snoozed`) el chip sigue —el silencio apaga el mail, no la plata
                afuera— pero sin relleno, como una celda crítica silenciada del Tablero (`-snoozed`). */}
            <button type="button" ref={chipRef}
                    className={`usp-chip ${levelModifier(current.level)}${current.is_snoozed === true ? ' -snoozed' : ''}`}
                    onClick={toggle} aria-haspopup="dialog" aria-expanded={open}
                    aria-controls={open ? popoverId : undefined} aria-describedby={tipId}
                    data-testid="uncovered-spend-chip">
                <EyeSlash size={14} weight="bold" aria-hidden="true"/>
                <span className="usp-chip__text">{label}</span>
            </button>
            <span className="warning-tooltip -below-left usp-chip__tip" role="tooltip" id={tipId}>
                <span className="warning-tooltip-body">
                    <span className="warning-tooltip-text">{t('line_uncovered.chip_tooltip')}</span>
                </span>
            </span>
            {open && (
                <UncoveredSpendPopover id={popoverId} uncovered={current} accounts={accounts}
                                       businessUnitId={businessUnitId} anchorRef={chipRef} onClose={close}
                                       onDismiss={dismiss} busyKey={busyKey}
                                       uncontrolledSpendEnabled={uncontrolledSpendEnabled}/>
            )}
        </span>
    );
    return rowClassName ? <div className={rowClassName}>{chip}</div> : chip;
}

export default UncoveredSpendChip;
